import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faFileDownload,
  faChevronLeft,
  faChevronRight,
  faExpand,
  faCompress,
  faSpinner,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faImage,
  faFile,
  faSearchPlus,
  faSearchMinus,
  faSync,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import { getFullFileUrl } from '../../Helpers/UrlHelpers';
import toast from 'react-hot-toast';
import '../../assets/css/FileViewer.css';

const FileViewer = ({ isOpen, onClose, files, currentIndex = 0 }) => {
  const [activeIndex, setActiveIndex] = useState(currentIndex);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Reset to new currentIndex when prop or open state changes
  useEffect(() => {
    if (isOpen) {
      setActiveIndex(currentIndex);
    }
  }, [currentIndex, isOpen]);

  // Reset view state on every file change
  useEffect(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setIsLoading(true);
    setError(null);
  }, [activeIndex, isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setActiveIndex(i => Math.max(i - 1, 0));
      if (e.key === 'ArrowRight') setActiveIndex(i => Math.min(i + 1, (files?.length ?? 1) - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, files?.length]);

  if (!isOpen || !files || files.length === 0) return null;

  const validIndex = Math.min(Math.max(0, activeIndex), files.length - 1);
  const currentFile = files[validIndex];
  if (!currentFile || !currentFile.file_name) return null;

  const cleanFileName = (currentFile.file_name || '').split('?')[0];
  const fileExtension = cleanFileName.split('.').pop().toLowerCase();
  const fileName = cleanFileName.split(/[\/\\]/).pop();
  const fullUrl = getFullFileUrl(currentFile.file_name);

  const isImageFile = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(fileExtension);
  const isPdfFile = fileExtension === 'pdf';
  const isWordFile = ['doc', 'docx'].includes(fileExtension);
  const isExcelFile = ['xls', 'xlsx', 'csv'].includes(fileExtension);
  const isPptFile = ['ppt', 'pptx'].includes(fileExtension);
  const isOfficeFile = isWordFile || isExcelFile || isPptFile;
  const isEmbeddable = isPdfFile || isOfficeFile;

  const getTypeInfo = () => {
    if (isPdfFile) return { icon: faFilePdf, color: '#f87171', bg: 'rgba(239,68,68,0.18)' };
    if (isImageFile) return { icon: faImage, color: '#fbbf24', bg: 'rgba(245,158,11,0.18)' };
    if (isWordFile) return { icon: faFileWord, color: '#60a5fa', bg: 'rgba(59,130,246,0.18)' };
    if (isExcelFile) return { icon: faFileExcel, color: '#34d399', bg: 'rgba(16,185,129,0.18)' };
    if (isPptFile) return { icon: faFilePowerpoint, color: '#fb923c', bg: 'rgba(249,115,22,0.18)' };
    return { icon: faFile, color: '#94a3b8', bg: 'rgba(148,163,184,0.18)' };
  };
  const typeInfo = getTypeInfo();

  const handleDownload = () => {
    try {
      const a = document.createElement('a');
      a.href = fullUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('جاري تحميل الملف...');
    } catch {
      toast.error('فشل في تحميل الملف');
    }
  };

  const handleZoomIn = () => setZoom(z => Math.min(z + 0.25, 4));
  const handleZoomOut = () => setZoom(z => Math.max(z - 0.25, 0.5));
  const handleResetZoom = () => { setZoom(1); setPosition({ x: 0, y: 0 }); };

  const handleWheel = (e) => {
    if (!isImageFile) return;
    e.preventDefault();
    setZoom(z => Math.min(Math.max(z + (e.deltaY > 0 ? -0.1 : 0.1), 0.5), 4));
  };

  const handleMouseDown = (e) => {
    if (zoom <= 1) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => setIsDragging(false);

  const getEmbedUrl = () =>
    isPdfFile
      ? fullUrl
      : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fullUrl)}`;

  /* ── Content renderer ─────────────────────────────────────── */
  const renderContent = () => {
    if (error) {
      return (
        <div className="fv-state-box">
          <motion.div
            className="fv-state-icon fv-state-error"
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <FontAwesomeIcon icon={faExclamationCircle} />
          </motion.div>
          <p className="fv-state-title">تعذّر تحميل الملف</p>
          <p className="fv-state-sub">{error}</p>
          <button onClick={handleDownload} className="fv-action-btn">
            <FontAwesomeIcon icon={faFileDownload} />
            <span>تحميل الملف</span>
          </button>
        </div>
      );
    }

    if (isImageFile) {
      return (
        <div
          className={`fv-image-wrap${zoom > 1 ? ' fv-grab' : ''}${isDragging ? ' fv-grabbing' : ''}`}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <AnimatePresence>
            {isLoading && (
              <motion.div key="loader" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fv-loader">
                <motion.span animate={{ rotate: 360 }} transition={{ duration: 0.9, repeat: Infinity, ease: 'linear' }}>
                  <FontAwesomeIcon icon={faSpinner} />
                </motion.span>
              </motion.div>
            )}
          </AnimatePresence>
          <img
            key={fullUrl}
            src={fullUrl}
            alt={fileName}
            className="fv-image"
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
              opacity: isLoading ? 0 : 1,
            }}
            onLoad={() => setIsLoading(false)}
            onError={() => { setIsLoading(false); setError('فشل في تحميل الصورة'); }}
            draggable={false}
          />
        </div>
      );
    }

    if (isEmbeddable) {
      return (
        <div className="fv-embed-wrap">
          <AnimatePresence>
            {isLoading && (
              <motion.div key="loader" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fv-loader">
                <motion.span animate={{ rotate: 360 }} transition={{ duration: 0.9, repeat: Infinity, ease: 'linear' }}>
                  <FontAwesomeIcon icon={faSpinner} />
                </motion.span>
                <span className="fv-loader-text">جاري تحميل الملف...</span>
              </motion.div>
            )}
          </AnimatePresence>
          <iframe
            key={activeIndex}
            src={getEmbedUrl()}
            title={fileName}
            className="fv-iframe"
            onLoad={() => setIsLoading(false)}
            onError={() => { setIsLoading(false); setError('فشل في تحميل الملف'); }}
          />
        </div>
      );
    }

    // Unsupported format
    return (
      <div className="fv-state-box">
        <motion.div
          className="fv-state-icon"
          style={{ color: typeInfo.color, background: typeInfo.bg }}
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <FontAwesomeIcon icon={typeInfo.icon} />
        </motion.div>
        <p className="fv-state-title">صيغة الملف غير مدعومة للعرض</p>
        <p className="fv-state-sub">{fileExtension.toUpperCase()} — {fileName}</p>
        <p className="fv-state-note">يمكنك تحميل الملف وفتحه من جهازك</p>
        <button onClick={handleDownload} className="fv-action-btn">
          <FontAwesomeIcon icon={faFileDownload} />
          <span>تحميل الملف</span>
        </button>
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="fv-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fv-overlay"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.93, y: 28 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.93, y: 28 }}
            transition={{ type: 'spring', damping: 28, stiffness: 350 }}
            className={`fv-modal${isFullscreen ? ' fv-fullscreen' : ''}`}
            onClick={e => e.stopPropagation()}
          >
            {/* ── Header ── */}
            <div className="fv-header">
              <div className="fv-hd-info">
                <span className="fv-hd-icon" style={{ background: typeInfo.bg, color: typeInfo.color }}>
                  <FontAwesomeIcon icon={typeInfo.icon} />
                </span>
                <div>
                  <p className="fv-hd-name" title={fileName}>{fileName}</p>
                  {files.length > 1 && (
                    <p className="fv-hd-count">{activeIndex + 1} من {files.length}</p>
                  )}
                </div>
              </div>

              <div className="fv-hd-actions">
                {isImageFile && (
                  <div className="fv-zoom-bar">
                    <button onClick={handleZoomOut} disabled={zoom <= 0.5} className="fv-hd-btn" title="تصغير">
                      <FontAwesomeIcon icon={faSearchMinus} />
                    </button>
                    <span className="fv-zoom-pct">{Math.round(zoom * 100)}%</span>
                    <button onClick={handleZoomIn} disabled={zoom >= 4} className="fv-hd-btn" title="تكبير">
                      <FontAwesomeIcon icon={faSearchPlus} />
                    </button>
                    {zoom !== 1 && (
                      <button onClick={handleResetZoom} className="fv-hd-btn" title="إعادة ضبط">
                        <FontAwesomeIcon icon={faSync} />
                      </button>
                    )}
                  </div>
                )}
                <button onClick={handleDownload} className="fv-hd-btn fv-hd-dl" title="تحميل">
                  <FontAwesomeIcon icon={faFileDownload} />
                </button>
                <button onClick={() => setIsFullscreen(f => !f)} className="fv-hd-btn" title={isFullscreen ? 'تصغير' : 'ملء الشاشة'}>
                  <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
                </button>
                <button onClick={onClose} className="fv-hd-btn fv-hd-close" title="إغلاق">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            {/* ── Body ── */}
            <div className="fv-body">
              {files.length > 1 && (
                <button
                  onClick={() => setActiveIndex(i => Math.max(i - 1, 0))}
                  disabled={activeIndex === 0}
                  className="fv-arrow fv-arrow-prev"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
              )}

              <div className="fv-stage">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIndex}
                    initial={{ opacity: 0, x: 14 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -14 }}
                    transition={{ duration: 0.18 }}
                    className="fv-stage-inner"
                  >
                    {renderContent()}
                  </motion.div>
                </AnimatePresence>
              </div>

              {files.length > 1 && (
                <button
                  onClick={() => setActiveIndex(i => Math.min(i + 1, files.length - 1))}
                  disabled={activeIndex === files.length - 1}
                  className="fv-arrow fv-arrow-next"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              )}
            </div>

            {/* ── Thumbnail Strip ── */}
            {files.length > 1 && (
              <div className="fv-thumbs">
                {files.map((file, i) => {
                  const ext = file.file_name.split('.').pop().toLowerCase();
                  const isImg = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
                  return (
                    <motion.button
                      key={i}
                      onClick={() => setActiveIndex(i)}
                      className={`fv-thumb${i === activeIndex ? ' fv-thumb-on' : ''}`}
                      whileHover={{ scale: 1.06 }}
                      whileTap={{ scale: 0.96 }}
                    >
                      {isImg
                        ? <img src={getFullFileUrl(file.file_name)} alt={`t-${i}`} className="fv-thumb-img" />
                        : <FontAwesomeIcon icon={faFile} className="fv-thumb-icon" />
                      }
                    </motion.button>
                  );
                })}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FileViewer;
